import { getTorontoHour, formatTorontoTime } from '../utils/torontoTime'

export function HistoricalContext({ effectiveTime, sessions, starredSessions, isDevMode }) {
  const currentHour = getTorontoHour(effectiveTime)
  
  // Calculate historical insights
  const totalSessions = sessions.length
  const totalStarred = starredSessions.length
  const starredPercentage = Math.round((totalStarred / totalSessions) * 100)
  
  // Get category distribution
  const categoryStats = sessions.reduce((acc, session) => {
    acc[session.category] = (acc[session.category] || 0) + 1
    return acc
  }, {})
  
  // Get historical conference patterns
  const getHistoricalPattern = (hour) => {
    if (hour < 9) return { period: 'Pre-Conference', attendance: 'Low', energy: 'Setup Mode', tip: 'Perfect time to review your starred sessions' }
    if (hour >= 9 && hour < 11) return { period: 'Morning Rush', attendance: 'High', energy: 'Fresh & Excited', tip: 'Best time for networking and meeting new people' }
    if (hour >= 11 && hour < 13) return { period: 'Peak Energy', attendance: 'Maximum', energy: 'Highest Engagement', tip: 'Prime time for key sessions and announcements' }
    if (hour >= 13 && hour < 15) return { period: 'Lunch Network', attendance: 'Medium', energy: 'Social Buzz', tip: 'Great for casual conversations and building connections' }
    if (hour >= 15 && hour < 17) return { period: 'Deep Focus', attendance: 'High', energy: 'Learning Mode', tip: 'Technical sessions and hands-on workshops shine now' }
    return { period: 'Wind Down', attendance: 'Decreasing', energy: 'Reflection', tip: 'Time to plan follow-ups and digest what you learned' }
  }
  
  const currentPattern = getHistoricalPattern(currentHour)
  
  // Get conference insights
  const insights = [
    {
      icon: '📊',
      title: 'Your Conference Planning',
      value: `${starredPercentage}%`,
      description: `You've starred ${totalStarred} of ${totalSessions} sessions`,
      color: 'text-editions-orange'
    },
    {
      icon: '🎯',
      title: 'Peak Attendance Time',
      value: '11:00 AM',
      description: 'Historically highest engagement period',
      color: 'text-editions-green'
    },
    {
      icon: '⚡',
      title: 'Current Energy Level',
      value: currentPattern.energy,
      description: `${currentPattern.period} - ${currentPattern.attendance} attendance`,
      color: 'text-editions-purple-light'
    },
    {
      icon: '💡',
      title: 'Pro Tip',
      value: currentPattern.tip,
      description: 'Based on conference patterns',
      color: 'text-yellow-400'
    }
  ]
  
  // Historical conference facts
  const conferenceFacts = [
    { icon: '🌍', fact: 'Shopify powers 5.6M active stores in 175+ countries' },
    { icon: '💰', fact: '$11.5B in BFCM 2024 sales, up 24% - 76M+ buyers' },
    { icon: '🚀', fact: 'Editions events shape commerce - Summer \'25 delivers' },
    { icon: '🇨🇦', fact: 'Born in Ottawa 2006, now $8.88B revenue in 2024' },
    { icon: '👥', fact: '16,000+ apps ecosystem with $1B+ payouts to developers' },
    { icon: '⚡', fact: '284M requests/min at peak - 1.19 trillion edge requests' }
  ]
  
  if (!isDevMode) return null
  
  return (
    <div className="bg-gradient-to-r from-indigo-500/10 to-purple-500/10 backdrop-blur-lg border border-indigo-500/30 rounded-xl p-4 mb-4">
      <div className="flex items-center gap-2 mb-4">
        <span className="text-xl">📚</span>
        <h3 className="text-white font-semibold">Conference Intelligence</h3>
        <div className="flex-1 h-px bg-indigo-500/30"></div>
        <span className="text-xs text-gray-400">Historical Context</span>
      </div>
      
      {/* Current Period Insights */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        {insights.map((insight, index) => (
          <div key={index} className="bg-white/5 rounded-lg p-3 border border-white/10">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-sm">{insight.icon}</span>
              <span className="text-xs text-gray-400">{insight.title}</span>
            </div>
            <div className={`text-sm font-medium ${insight.color} mb-1`}>
              {insight.value}
            </div>
            <div className="text-xs text-gray-300">
              {insight.description}
            </div>
          </div>
        ))}
      </div>
      
      {/* Category Distribution */}
      <div className="mb-4">
        <div className="text-xs text-gray-400 mb-2 flex items-center gap-2">
          <span>📈</span>
          <span>Session Distribution</span>
        </div>
        <div className="flex flex-wrap gap-2">
          {Object.entries(categoryStats).map(([category, count]) => (
            <div key={category} className="flex items-center gap-2 bg-white/5 rounded-lg px-3 py-2">
              <div className={`w-2 h-2 rounded-full ${
                category === 'Dev mode' ? 'bg-editions-green' :
                category === 'Biz mode' ? 'bg-editions-purple' : 'bg-gray-400'
              }`}></div>
              <span className="text-xs text-white">{category}</span>
              <span className="text-xs text-gray-400">({count})</span>
            </div>
          ))}
        </div>
      </div>
      
      {/* Shopify & Conference Facts */}
      <div className="border-t border-white/10 pt-4">
        <div className="text-xs text-gray-400 mb-3 flex items-center gap-2">
          <span>🎓</span>
          <span>Did You Know?</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {conferenceFacts.map((item, index) => (
            <div key={index} className="text-xs text-gray-300 flex items-center gap-2 bg-white/5 rounded-md px-2 py-1">
              <span>{item.icon}</span>
              <span>{item.fact}</span>
            </div>
          ))}
        </div>
      </div>
      
      {/* Time-specific Recommendations */}
      <div className="border-t border-white/10 pt-4 mt-4">
        <div className="bg-indigo-500/10 rounded-lg p-3 border border-indigo-500/20">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-sm">🎯</span>
            <span className="text-sm text-white font-medium">Current Period Strategy</span>
          </div>
          <div className="text-xs text-gray-300 mb-1">
            {formatTorontoTime(effectiveTime)} - {currentPattern.period}
          </div>
          <div className="text-xs text-indigo-300">
            {currentPattern.tip}
          </div>
        </div>
      </div>
    </div>
  )
} 